/**
 * BundleSuite BYOB — open theme cart drawer after bundle add (stay_on_page mode).
 * Uses cart.js as source of truth; injects bundle-components breakdown if section HTML omits it.
 *
 * Full-page BYOB (.bundlesuite-container): xboost-need-refresh-and-open-cart
 * In Page BYOB (.inline-byob-container / october-flash-sale): cart:refresh + UpdateCart
 * (In Page does not fire the xboost event — confirmed on flash page.)
 */
(function () {
  const CONTAINER = '.bundlesuite-container';
  const INLINE = '.inline-byob-container';
  const FLASH_PATH = '/products/october-flash-sale';
  const EVENT = 'xboost-need-refresh-and-open-cart';
  const INLINE_EVENTS = ['cart:refresh', 'UpdateCart'];
  const SETTLE_MS = 150;
  const INLINE_SETTLE_MS = 250;
  const INJECTED_ATTR = 'data-byob-included-injected';

  let listenerBound = false;
  let refreshInFlight = false;

  function isInlineByobPage() {
    return (
      !!document.querySelector(INLINE) ||
      window.location.pathname.indexOf(FLASH_PATH) !== -1
    );
  }

  function isByobPage() {
    return !!document.querySelector(CONTAINER) || isInlineByobPage();
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function getIncludedProductsLabel() {
    return (
      window.BYOBConfig?.styles?.css?.includedProductsText || 'Included Products'
    );
  }

  function findBundleLineItem(cart) {
    if (!cart?.items?.length) return null;

    let index = cart.items.findIndex((item) => {
      const props = item.properties || {};
      return props.__byob != null && props.__byob !== '';
    });

    if (index === -1) {
      const variantId = window.BSGlobal?.product?.variants?.[0]?.id;
      if (variantId != null) {
        index = cart.items.findIndex(
          (item) => String(item.variant_id) === String(variantId)
        );
      }
    }

    if (index === -1) return null;

    return { item: cart.items[index], line: index + 1 };
  }

  function getIncludedProductsValue(bundleItem) {
    if (!bundleItem) return '';

    const label = getIncludedProductsLabel();
    const value = bundleItem.properties?.[label];

    if (value == null || String(value).trim() === '') return '';

    return String(value);
  }

  function parseIncludedProductsString(value) {
    if (!value) return [];

    const parts = [];
    let current = '';
    let depth = 0;

    for (let i = 0; i < value.length; i += 1) {
      const ch = value[i];
      if (ch === '(' || ch === '[') depth += 1;
      else if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1);
      else if (ch === ',' && depth === 0) {
        if (current.trim()) parts.push(current.trim());
        current = '';
        continue;
      }
      current += ch;
    }

    if (current.trim()) parts.push(current.trim());

    return parts.map(parseIncludedProductsSegment).filter(Boolean);
  }

  function parseIncludedProductsSegment(segment) {
    let text = segment.trim();
    if (!text) return null;

    let quantity = 1;
    const qtyMatch = text.match(/\sx(\d+)$/i);
    if (qtyMatch) {
      quantity = parseInt(qtyMatch[1], 10) || 1;
      text = text.slice(0, qtyMatch.index).trim();
    }

    text = text.replace(/\s*\[[^\]]+\]\s*$/, '').trim();

    let title = text;
    let variant = '';
    const variantMatch = text.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (variantMatch) {
      title = variantMatch[1].trim();
      variant = variantMatch[2].trim();
    }

    return { title, variant, quantity };
  }

  function parseByobPayload(bundleItem) {
    const raw = bundleItem?.properties?.__byob;
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (parsed?.v === 2 && Array.isArray(parsed.c)) return parsed;
      if (Array.isArray(parsed?.items)) return { c: parsed.items };
      return null;
    } catch (_err) {
      return null;
    }
  }

  function getSideCartImageMap() {
    const map = new Map();

    /* Full-page BYOB side cart */
    document
      .querySelectorAll(`${CONTAINER} .sc-product-item[data-variant-id]`)
      .forEach((el) => {
        const variantId = el.getAttribute('data-variant-id');
        if (!variantId) return;

        map.set(String(variantId), {
          image: el.querySelector('img')?.getAttribute('src') || '',
          title: el.querySelector('.scpt-title h4')?.textContent?.trim() || '',
          variant: el.querySelector('.scpt-variant p')?.textContent?.trim() || '',
        });
      });

    /* In Page BYOB product cards — original bridge only scraped full-page side cart */
    document.querySelectorAll(`${INLINE} .inline-byob-product-card`).forEach((el) => {
      const variantId =
        el.getAttribute('data-selected-variant-id') ||
        el.getAttribute('data-variant-id');
      const img =
        el.querySelector('.product-image img, img')?.getAttribute('src') || '';
      const title = el.querySelector('.product-title')?.textContent?.trim() || '';
      if (!variantId || !img) return;

      map.set(String(variantId), {
        image: img,
        title,
        variant: '',
      });
    });

    return map;
  }

  function collectInlineProductHandles() {
    const handles = new Set();
    document
      .querySelectorAll(`${INLINE} a[href*="/products/"]`)
      .forEach((link) => {
        try {
          const path = new URL(link.href, window.location.origin).pathname;
          const match = path.match(/\/products\/([^/?#]+)/);
          if (match) handles.add(decodeURIComponent(match[1]));
        } catch (_err) {
          /* ignore */
        }
      });
    return [...handles];
  }

  async function enrichImageMapFromProducts(map) {
    const handles = collectInlineProductHandles();
    if (!handles.length) return map;

    await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(
            `/products/${encodeURIComponent(handle)}.js`,
            { credentials: 'same-origin' }
          );
          if (!res.ok) return;
          const product = await res.json();
          const featured = product.featured_image;
          const fallback =
            (typeof featured === 'string' && featured) ||
            featured?.src ||
            (Array.isArray(product.images) &&
              (typeof product.images[0] === 'string'
                ? product.images[0]
                : product.images[0]?.src)) ||
            '';

          (product.variants || []).forEach((variant) => {
            const id = String(variant.id);
            if (map.has(id) && map.get(id).image) return;

            const vImg = variant.featured_image;
            const image =
              (typeof vImg === 'string' && vImg) ||
              vImg?.src ||
              fallback ||
              '';

            map.set(id, {
              image,
              title: product.title || map.get(id)?.title || '',
              variant:
                variant.title && variant.title !== 'Default Title'
                  ? variant.title
                  : map.get(id)?.variant || '',
            });
          });
        } catch (_err) {
          /* ignore fetch errors */
        }
      })
    );

    return map;
  }

  function normalizeComponentImage(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  }

  async function buildComponents(bundleItem, includedValue) {
    const itemComponents = bundleItem?.item_components;
    if (Array.isArray(itemComponents) && itemComponents.length) {
      return itemComponents.map((component) => ({
        title:
          component.product_title ||
          component.title ||
          component.untranslated_product_title ||
          '',
        variant:
          component.variant_title &&
          component.variant_title !== 'Default Title'
            ? component.variant_title
            : component.untranslated_variant_title || '',
        quantity: component.quantity || 1,
        image: normalizeComponentImage(
          component.image || component.featured_image
        ),
      }));
    }

    const parsedFromText = parseIncludedProductsString(includedValue);
    const byob = parseByobPayload(bundleItem);
    let imageMap = getSideCartImageMap();

    /* Flash In Page: cart.js has no item_components; fill images via cards + product.js */
    if (byob?.c?.length) {
      const missing = byob.c.some((entry) => !imageMap.get(String(entry[0]))?.image);
      if (missing) imageMap = await enrichImageMapFromProducts(imageMap);

      return byob.c.map((entry, index) => {
        const variantId = String(entry[0]);
        const sideCart = imageMap.get(variantId) || {};
        const parsed = parsedFromText[index] || {};

        return {
          title: sideCart.title || parsed.title || '',
          variant: sideCart.variant || parsed.variant || '',
          quantity: entry[1] || parsed.quantity || 1,
          image: sideCart.image || '',
        };
      });
    }

    return parsedFromText.map((component) => ({
      ...component,
      image: '',
    }));
  }

  function getComponentItemCount(components) {
    return components.reduce(
      (sum, component) => sum + (component.quantity || 1),
      0
    );
  }

  function lineHasBreakdown(lineEl) {
    if (!lineEl) return false;

    if (lineEl.querySelector('.bundle-components-toggle')) return true;
    if (lineEl.querySelector(`[${INJECTED_ATTR}]`)) return true;

    return false;
  }

  function removePlainIncludedProductsOption(lineEl, label) {
    const labelLower = label.toLowerCase();

    lineEl.querySelectorAll('.product-option').forEach((option) => {
      const name = option.querySelector('.product-option__name');
      if (!name) return;

      const text = name.textContent.trim().toLowerCase().replace(/:$/, '');
      if (text === labelLower) option.remove();
    });
  }

  function bindBundleToggle(button, list, itemCount) {
    const toggleText = button.querySelector('.toggle-text');

    const toggle = () => {
      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        list.style.display = 'none';
        button.setAttribute('aria-expanded', 'false');
        toggleText.textContent = `Show ${itemCount} items`;
      } else {
        list.style.display = 'block';
        button.setAttribute('aria-expanded', 'true');
        toggleText.textContent = `Hide ${itemCount} items`;
      }
    };

    if (typeof window.toggleBundleComponents === 'function') {
      button.addEventListener('click', () => window.toggleBundleComponents(button));
      return;
    }

    button.addEventListener('click', toggle);
  }

  function createBundleItemElement(component) {
    const item = document.createElement('div');
    item.className = 'bundle-item';

    const imageWrap = document.createElement('div');
    imageWrap.className = 'bundle-item-image';

    if (component.image) {
      const img = document.createElement('img');
      img.src = component.image;
      img.alt = component.title || '';
      img.loading = 'lazy';
      imageWrap.appendChild(img);
    } else {
      imageWrap.classList.add('no-image');
    }

    const details = document.createElement('div');
    details.className = 'bundle-item-details';

    const info = document.createElement('div');
    info.className = 'bundle-item-info';

    const quantity = document.createElement('span');
    quantity.className = 'bundle-item-quantity';
    quantity.textContent = String(component.quantity || 1);

    const title = document.createElement('span');
    title.className = 'bundle-item-title';
    title.textContent = component.title || '';

    info.appendChild(quantity);
    info.appendChild(title);
    details.appendChild(info);

    if (component.variant) {
      const variant = document.createElement('div');
      variant.className = 'bundle-item-variant';
      variant.textContent = component.variant;
      details.appendChild(variant);
    }

    item.appendChild(imageWrap);
    item.appendChild(details);

    return item;
  }

  async function injectBundleComponentsBreakdown(lineEl, bundleItem, label, value) {
    if (!lineEl || !value || lineHasBreakdown(lineEl)) return false;

    const components = (await buildComponents(bundleItem, value)).filter(
      (component) => component.title
    );
    if (!components.length) return false;

    const info = lineEl.querySelector('.cart-item__info');
    if (!info) return false;

    removePlainIncludedProductsOption(lineEl, label);

    const itemCount = getComponentItemCount(components);
    const wrapper = document.createElement('div');
    wrapper.className = 'bundle-components-toggle';
    wrapper.setAttribute(INJECTED_ATTR, 'true');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bundle-toggle-btn';
    button.setAttribute('aria-expanded', 'true');

    const toggleText = document.createElement('span');
    toggleText.className = 'toggle-text';
    toggleText.textContent = `Hide ${itemCount} items`;

    const toggleIcon = document.createElement('span');
    toggleIcon.className = 'toggle-icon';
    toggleIcon.setAttribute('aria-hidden', 'true');
    toggleIcon.innerHTML =
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    button.appendChild(toggleText);
    button.appendChild(toggleIcon);

    const list = document.createElement('div');
    list.className = 'bundle-components-list';
    list.style.display = 'block';

    components.forEach((component) => {
      list.appendChild(createBundleItemElement(component));
    });

    bindBundleToggle(button, list, itemCount);

    wrapper.appendChild(button);
    wrapper.appendChild(list);

    const anchor =
      info.querySelector('.title-price-wrapper') ||
      info.querySelector('.cart-item__name');

    if (anchor?.parentElement) {
      anchor.parentElement.insertAdjacentElement('afterend', wrapper);
    } else {
      info.appendChild(wrapper);
    }

    return true;
  }

  async function fetchCartJson() {
    const response = await fetch(`${window.routes.cart_url}.js`);
    if (!response.ok) throw new Error('cart.js fetch failed');
    return response.json();
  }

  async function fetchCartSections(cartDrawer) {
    const sectionIds = cartDrawer
      .getSectionsToRender()
      .map((section) => section.id);
    const url = `${window.routes.cart_url}?sections=${sectionIds.join(',')}&sections_url=${encodeURIComponent('/cart')}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('cart sections fetch failed');
    const data = await response.json();
    return data.sections || data;
  }

  function renderCartDrawer(cartDrawer, sections) {
    if (!sections?.['cart-drawer']) return;

    if (cartDrawer.classList.contains('is-empty')) {
      cartDrawer.classList.remove('is-empty');
    }

    cartDrawer.renderContents({ sections });
  }

  async function refreshAndOpenCartDrawer() {
    if (!isByobPage() || refreshInFlight) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
    if (!window.routes?.cart_url) return;

    refreshInFlight = true;

    try {
      await sleep(isInlineByobPage() ? INLINE_SETTLE_MS : SETTLE_MS);

      const cart = await fetchCartJson();
      const bundleMatch = findBundleLineItem(cart);
      const includedLabel = getIncludedProductsLabel();
      const includedValue = getIncludedProductsValue(bundleMatch?.item);

      if (!bundleMatch) {
        console.warn('[byob-cart-drawer-bridge] No BYOB line item found in cart.js');
      } else if (!includedValue) {
        console.warn(
          '[byob-cart-drawer-bridge] Included Products property missing from cart.js'
        );
      }

      const sections = await fetchCartSections(cartDrawer);
      renderCartDrawer(cartDrawer, sections);

      if (bundleMatch && includedValue) {
        const lineEl = document.getElementById(
          `CartDrawer-Item-${bundleMatch.line}`
        );

        if (!lineEl) {
          console.warn(
            '[byob-cart-drawer-bridge] Could not match BYOB line in drawer DOM',
            { line: bundleMatch.line }
          );
        } else {
          await injectBundleComponentsBreakdown(
            lineEl,
            bundleMatch.item,
            includedLabel,
            includedValue
          );
        }
      }
    } catch (err) {
      console.error('[byob-cart-drawer-bridge]', err);
    } finally {
      refreshInFlight = false;
    }
  }

  function bindListener() {
    if (listenerBound || !isByobPage()) return;

    listenerBound = true;
    document.addEventListener(EVENT, refreshAndOpenCartDrawer);

    /* In Page widget refreshes cart sections but never opens theme cart-drawer */
    if (isInlineByobPage()) {
      INLINE_EVENTS.forEach((name) => {
        document.addEventListener(name, refreshAndOpenCartDrawer);
      });
    }
  }

  function init() {
    bindListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('bundleInitialized', init);
})();
